import { PlusCircleIcon } from "@heroicons/react/20/solid";
import StickyWrapper from "common/components/StickyWrapper/StickyWrapper";
import { Link } from "react-router-dom";
import type { IconNames } from "components/Icon/Icon";
import Button from "common/components/Button/Button";
import Icon from "components/Icon/Icon";
import CrudPagination from "./CrudPagination";
import CrudFilters, { ICrudFiltersProps } from "./CrudFilters";

export type DefaultHeaderAction = "openSidebar" | "openModal" | "openPage";

export type CrudHeaderAction = {
  title: string;
  icon?: IconNames;
  action?: DefaultHeaderAction | (() => void);
  link?: string;
  disabled?: boolean;
  type?: string;
  className?: string;
  compact?: boolean;
};

export type CrudHeaderProps = {
  title: string;
  subtitle?: string;
  icon: IconNames;
  headerActions?: CrudHeaderAction[];
  compact?: boolean;
  colorMode?: "light" | "dark";
  pageCount: number;
  itemsPerPage?: number;
  itemsCount?: number;
  setPageIndex: (pageIndex: number) => void;
  pageIndex: number;
  isFetching?: boolean;
  viewToggle?: React.ReactNode;
} & ICrudFiltersProps;

function CrudHeader({
  title,
  subtitle,
  icon,
  headerActions = [],
  compact = false,
  colorMode = "dark",
  pageIndex,
  setPageIndex,
  pageCount: controlledPageCount,
  itemsPerPage = 0,
  itemsCount = 0,
  isFetching = false,
  viewToggle,
  ...filtersProps
}: CrudHeaderProps) {
  const isLightMode = colorMode === "light";
  const titleClass = isLightMode ? "text-white" : "text-primary";
  const subtitleClass = isLightMode ? "text-white/90" : "text-slate-500";
  const iconWrapperClass = isLightMode
    ? "bg-primary-50/20 border-primary-200"
    : "bg-primary-500 border-primary-700";
  const iconClass = isLightMode ? "text-white" : "text-primary-700";
  const addButtonClass = isLightMode
    ? "bg-primary-50 text-primary hover:scale-[1.01]"
    : "bg-primary-50 hover:bg-primary-100 text-primary border border-primary-200";
  const addButtonIconClass = isLightMode ? "text-primary" : "text-white";
  const addActionClassName = isLightMode
    ? "text-primary bg-primary-50 hover:scale-[1.01]"
    : "text-white bg-primary border border-primary-600 hover:bg-primary-600";

  return (
    <StickyWrapper isHeader isBeforeFilters={false}>
      {/** Header */}
      <div className="flex flex-col justify-center relative lg:min-h-[70px] gap-4 px-4">
        <div className="flex flex-col lg:flex-row justify-between lg:items-center px-4 py-4 w-full">
          <div className="flex-1">
            <div className="flex flex-row gap-4">
              <div
                className={`flex items-center font-bold gap-4 ${titleClass} ${
                  compact ? "text-md" : "text-2xl"
                }`}
              >
                {!compact && (
                  <div
                    className={`p-1 rounded-lg border-[1px] ${iconWrapperClass}`}
                  >
                    <Icon name={icon} className={`w-7 h-7 ${iconClass}`} />
                  </div>
                )}
                <div>
                  <h1 className={compact ? "text-md" : "text-3xl"}>{title}</h1>
                  {!compact && subtitle && (
                    <div className={`text-sm ${subtitleClass}`}>{subtitle}</div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="flex flex-row gap-3 items-center">
            {viewToggle}
            {controlledPageCount > 1 && (
              <CrudPagination
                setPageIndex={setPageIndex}
                totalPage={controlledPageCount}
                disabled={isFetching}
                pageIndex={pageIndex}
                itemsPerPage={itemsPerPage}
                itemsCount={itemsCount}
              />
            )}

            {/* Add buttons */}

            <div className="flex flex-col lg:flex-row gap-4">
              {headerActions.map(
                (
                  {
                    title: headerActionTitle,
                    icon: headerActionIcon,
                    action: headerAction,
                    link: headerActionLink,
                    disabled: headerActionDisabled,
                    compact: headerActionCompact,
                    type: headerActionType,
                    className: headerActionClassName,
                  },
                  index
                ) => {
                  return headerActionLink ? (
                    <Link
                      to={headerActionLink}
                      // eslint-disable-next-line react/no-array-index-key
                      key={`header-action-link-${index}`}
                      className={`${
                        headerActionDisabled ? "pointer-events-none" : ""
                      } flex justify-end self-start mt-3 md:mt-0 mb-4 lg:mb-0 ${headerActionClassName}`}
                    >
                      <div
                        className={`flex cursor-pointer px-3 py-2 gap-3 rounded-md ${addButtonClass}`}
                      >
                        {!headerActionCompact && (
                          <div
                            className={`flex flex-col justify-center text-left ${
                              headerActionDisabled && "opacity-40"
                            }`}
                          >
                            {headerActionTitle && (
                              <span className="font-bold text-right">
                                {headerActionTitle}
                              </span>
                            )}
                          </div>
                        )}
                        <div
                          className={`flex flex-col items-center justify-center text-left ${
                            headerActionDisabled && "opacity-40"
                          }`}
                        >
                          <PlusCircleIcon
                            className={`w-5 h-5 ${addButtonIconClass}`}
                          />
                        </div>
                      </div>
                    </Link>
                  ) : (
                    <Button
                      title={headerActionTitle}
                      icon={headerActionIcon}
                      onClick={headerAction as () => void}
                      disabled={headerActionDisabled}
                      type={headerActionType}
                      className={`${addActionClassName} ${headerActionClassName}`}
                      compact={headerActionCompact}
                    />
                  );
                }
              )}
            </div>
          </div>
        </div>
        <CrudFilters {...filtersProps} />
      </div>
    </StickyWrapper>
  );
}

export default CrudHeader;
